/**
 * "Cần nhắc" (N1 — docs/plans/pv2-n1-dat-lich.md): nhắc lịch MIỄN PHÍ bằng Zalo của tiệm.
 *
 * Một dòng ở đầu trang Lịch: "Ngày mai còn N lịch chưa nhắc". Bấm → danh sách; mỗi lịch một nút
 * "Chép tin & mở Zalo": chép tin soạn sẵn (có link khách bấm "Tôi vẫn đến" / đổi giờ) rồi mở khung
 * chat `zalo.me/<số>`; lễ tân chỉ việc dán và gửi. Máy chủ ghi "đã nhắc" để danh sách không hiện lại
 * và để job báo quầy khi khách chưa trả lời.
 *
 * `zalo.me/<số>` KHÔNG nhận nội dung soạn sẵn (docs/zalo/goi-oa-2026-va-nhan-tay.md mục 4) — nên chép
 * vào bộ nhớ tạm TRƯỚC khi mở, trong cùng một lần bấm (trình duyệt chỉ cho chép khi người dùng bấm).
 */
import { BellRing, Check, ChevronRight, Copy, MessageCircle, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { type Api, ApiFailure, type DongCanNhac, ngayVn } from './api.ts'
import { useCaiDatTiem } from './cai-dat-tiem.ts'
import { conLichCanBam } from './can-nhac.ts'
import { taiCoHuy } from './chi-nhanh-dang-dung.ts'
import { nhanNgayDayDu } from './ngay.ts'
import { T } from './text/vi.ts'
import { ThongBao, To } from './ui.tsx'

/** Ngày mai theo giờ VN, 'YYYY-MM-DD' (cộng ngày trên chuỗi, không qua múi giờ máy). */
function ngayMai(): string {
  const [y, m, d] = ngayVn()
    .split('-')
    .map((x) => Number.parseInt(x, 10)) as [number, number, number]
  const t = new Date(Date.UTC(y, m - 1, d + 1))
  return t.toISOString().slice(0, 10)
}

export function TheCanNhac({ api, branchId }: { api: Api; branchId: string }) {
  // Tiệm chọn nhắc "sáng cùng ngày" (0031 `moc_nhac`): danh sách là lịch HÔM NAY, không phải ngày mai.
  const cd = useCaiDatTiem()
  const ngay = cd?.datLich.mocNhac === 'sang_cung_ngay' ? ngayVn() : ngayMai()
  const [ds, setDs] = useState<DongCanNhac[] | null>(null)
  const [mo, setMo] = useState(false)
  /** Tăng lên để tải lại sau khi lễ tân nhắc xong (cùng chi nhánh, cùng ngày). */
  const [lanTai, setLanTai] = useState(0)
  const nap = useCallback(() => setLanTai((n) => n + 1), [])
  // biome-ignore lint/correctness/useExhaustiveDependencies: `lanTai` là TÍN HIỆU tải lại.
  useEffect(() => {
    // Danh sách của chi nhánh / ngày cũ về muộn bị bỏ (hàm dọn của taiCoHuy) — không đè thẻ của chi nhánh mới.
    return taiCoHuy(
      api.canNhac(branchId, ngay),
      (r) => setDs(r.lich),
      () => setDs(null),
    )
  }, [api, branchId, ngay, lanTai])

  const chuaNhac = ds?.filter((x) => !x.daNhac && !x.daXacNhan).length ?? 0
  if (ds === null || ds.length === 0) return null
  return (
    <>
      <button type="button" className="the the-can-nhac" onClick={() => setMo(true)}>
        <BellRing size={20} />
        <span>
          <b>{T.remindTitle}</b>
          <span className="chu-phu">{T.remindFor(nhanNgayDayDu(ngay))}</span>
        </span>
        <span className={`dem-can-nhac ${chuaNhac > 0 ? 'con' : ''}`}>
          {chuaNhac > 0 ? chuaNhac : <Check size={16} />}
        </span>
        <ChevronRight size={20} className="mui-ten-the" />
      </button>
      {mo && <ToCanNhac api={api} ds={ds} ngay={ngay} onDong={() => setMo(false)} onDaDoi={nap} />}
    </>
  )
}

const thuTuNhac = (x: DongCanNhac) => (x.daXacNhan ? 2 : x.daNhac ? 1 : 0)

function ToCanNhac({
  api,
  ds,
  ngay,
  onDong,
  onDaDoi,
}: {
  api: Api
  ds: DongCanNhac[]
  ngay: string
  onDong: () => void
  onDaDoi: () => void
}) {
  const [bao, setBao] = useState<{ chu: string; ok: boolean } | null>(null)
  const [loi, setLoi] = useState<string | null>(null)

  async function chep(x: DongCanNhac, moZalo: boolean) {
    setLoi(null)
    // Mở cửa sổ NGAY trong lần bấm (trình duyệt chặn popup mở sau một `await`).
    const cua = moZalo && x.sdtZalo !== null ? window.open('about:blank', '_blank') : null
    // Cắt `opener`: trang ngoài không điều hướng ngược được tab quầy (tabnabbing — soát N3 R8).
    if (cua !== null) cua.opener = null
    let daChep = true
    try {
      await navigator.clipboard.writeText(x.tin)
    } catch {
      daChep = false
    }
    if (cua !== null && x.sdtZalo !== null) cua.location.href = `https://zalo.me/${x.sdtZalo}`
    setBao({ chu: daChep ? T.remindCopied : T.remindCopyFailed, ok: daChep })
    try {
      await api.ghiDaNhac(x.appointmentId)
      onDaDoi()
    } catch (e) {
      setLoi(e instanceof ApiFailure ? e.messageVi : T.serverFailed)
    }
  }

  return (
    <To nhan={T.remindTitle} onDong={onDong}>
      <header className="to-dau">
        {/* Ngày thành dòng phụ riêng: không còn dấu "·" treo ở chỗ xuống dòng (ui-critic v23–24). */}
        <div>
          <h2>{T.remindTitle}</h2>
          <p className="chu-phu">{T.remindFor(nhanNgayDayDu(ngay))}</p>
        </div>
        <button
          type="button"
          className="nut nut-nhat nut-icon"
          aria-label={T.close}
          onClick={onDong}
        >
          <X size={22} />
        </button>
      </header>
      <div className="to-than">
        {/* Chỉ mời bấm nút khi còn nút để bấm: khách đã xác nhận thì nút ẩn (báo 27/10: "mất nút Chép tin"). */}
        <p className="chu-phu">{conLichCanBam(ds) ? T.remindHint : T.remindAllConfirmed}</p>
        {bao !== null && (
          // Chép hỏng thì là lời nhắc (vàng), không phải dấu tích xanh "thành công" (03/10).
          <ThongBao loai={bao.ok ? 'thanh-cong' : 'canh-bao'} onDong={() => setBao(null)}>
            {bao.chu}
          </ThongBao>
        )}
        {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}
        {ds.length === 0 ? (
          <p className="chu-phu">{T.remindNone}</p>
        ) : (
          <ul className="ds-can-nhac">
            {[...ds]
              // Việc còn phải làm lên trước: chưa nhắc → đã nhắc → khách đã xác nhận (giữ thứ tự giờ trong mỗi nhóm).
              .sort((p, q) => thuTuNhac(p) - thuTuNhac(q))
              .map((x) => (
                <li key={x.appointmentId}>
                  <div className="dong-can-nhac-dau">
                    <b>{x.batDau.slice(11, 16)}</b>
                    <span>{x.khach ?? T.walkInName}</span>
                    {x.daXacNhan ? (
                      <span className="nhan-xac-nhan ok">{T.attendanceConfirmed}</span>
                    ) : x.daNhac ? (
                      <span className="nhan-xac-nhan chua">{T.remindDone}</span>
                    ) : null}
                  </div>
                  <p className="chu-phu">{x.dichVu}</p>
                  {!x.daXacNhan && (
                    // Điện thoại: mỗi nút một hàng ("Chép tin & mở Zalo" không vừa nửa tờ — chủ dự án báo 03/10); tablet: cạnh nhau.
                    // "Chép tin" là nút PHỤ khi khách có số Zalo (một hành động chính mỗi thẻ — ui-critic v32).
                    <div className="hang-phu hang-nhac">
                      {x.sdtZalo !== null ? (
                        <button
                          type="button"
                          className={`nut ${x.daNhac ? 'nut-vien' : 'nut-chinh'}`}
                          onClick={() => void chep(x, true)}
                        >
                          <MessageCircle size={18} />
                          {T.remindCopyOpen}
                        </button>
                      ) : (
                        <span className="chu-phu">{T.remindNoPhone}</span>
                      )}
                      <button
                        type="button"
                        className={`nut ${x.sdtZalo !== null ? 'nut-nhat' : 'nut-vien'}`}
                        onClick={() => void chep(x, false)}
                      >
                        <Copy size={18} />
                        {T.remindCopyOnly}
                      </button>
                    </div>
                  )}
                </li>
              ))}
          </ul>
        )}
      </div>
    </To>
  )
}
